import { chart as rawChart } from '@rawgraphs/rawgraphs-core'
import { mapDataInWorker } from '../worker'
import { WEBWORKER_ACTIVE } from '../constants'
import {
  renderBeeswarmCanvas,
  renderPointMapCanvas,
} from './canvasPreview'
import { DEFAULT_BRAND_THEME, buildBrandColorScaleOption } from './brandTheme'
import { CHART_IDS } from '../charts/chartIds'
import { FAST_PREVIEW_ROW_THRESHOLD } from './previewRendererMode'

const DIRECT_CANVAS_EXPORT_CHART_IDS = new Set([
  CHART_IDS.POINT_MAP,
  CHART_IDS.BEESWARM,
])

export function canDirectCanvasExport(chart) {
  return DIRECT_CANVAS_EXPORT_CHART_IDS.has(chart?.metadata?.id || '')
}

function getHeaderMetrics(theme = DEFAULT_BRAND_THEME) {
  const title = theme?.title || ''
  const subtitle = theme?.subtitle || ''
  const hasHeader = Boolean(title || subtitle)
  const titlePadding = title ? 34 : 0
  const subtitlePadding = subtitle ? 22 : 0
  const headerPadding = hasHeader ? titlePadding + subtitlePadding + 18 : 0

  return {
    title,
    subtitle,
    hasHeader,
    headerPadding,
  }
}

function composeCanvasWithBrandHeader(chartCanvas, visualOptions, brandTheme) {
  const theme = { ...DEFAULT_BRAND_THEME, ...brandTheme }
  const { backgroundColor, textColor, secondaryColor, fontFamily } = theme
  const { title, subtitle, hasHeader, headerPadding } = getHeaderMetrics(theme)
  const logicalWidth = visualOptions.width || chartCanvas.width
  const logicalHeight = visualOptions.height || chartCanvas.height

  if (!hasHeader) {
    return chartCanvas
  }

  const outputCanvas = document.createElement('canvas')
  const ratio = chartCanvas.width / Math.max(1, logicalWidth)
  const outputHeight = logicalHeight + headerPadding
  const headerScale = Math.max(0.7, Math.min(1.8, logicalWidth / 1200))

  outputCanvas.width = Math.round(logicalWidth * ratio)
  outputCanvas.height = Math.round(outputHeight * ratio)
  outputCanvas.style.width = `${logicalWidth}px`
  outputCanvas.style.height = `${outputHeight}px`

  const ctx = outputCanvas.getContext('2d')
  ctx.setTransform(ratio, 0, 0, ratio, 0, 0)
  ctx.fillStyle = backgroundColor
  ctx.fillRect(0, 0, logicalWidth, outputHeight)

  ctx.fillStyle = textColor
  ctx.textBaseline = 'alphabetic'

  const headerInset = Math.round(24 * headerScale)
  const headerTopOffset = Math.round(28 * headerScale)
  const titleFontSize = Math.round(26 * headerScale)
  const subtitleFontSize = Math.round(14 * headerScale)

  if (title) {
    ctx.font = `700 ${titleFontSize}px ${fontFamily}`
    ctx.fillText(title, headerInset, headerTopOffset)
  }

  if (subtitle) {
    ctx.font = `${subtitleFontSize}px ${fontFamily}`
    ctx.fillText(subtitle, headerInset, title ? headerTopOffset + Math.round(24 * headerScale) : headerTopOffset)
  }

  const subtitleY = title ? Math.round(24 * headerScale) : 0
  const lineY = title
    ? subtitle
      ? headerTopOffset + subtitleY + Math.round(14 * headerScale)
      : headerTopOffset + Math.round(18 * headerScale)
    : headerTopOffset + Math.round(14 * headerScale)
  ctx.strokeStyle = secondaryColor || textColor
  ctx.lineWidth = Math.max(1, Math.round(2.5 * headerScale))
  ctx.beginPath()
  ctx.moveTo(headerInset, lineY)
  ctx.lineTo(logicalWidth - headerInset, lineY)
  ctx.stroke()

  ctx.drawImage(chartCanvas, 0, headerPadding, logicalWidth, logicalHeight)
  return outputCanvas
}

async function buildMappedData({ chart, dataset, dataTypes, mapping, visualOptions }) {
  const viz = rawChart(chart, {
    data: dataset,
    mapping,
    dataTypes,
    visualOptions,
  })

  return WEBWORKER_ACTIVE
    ? mapDataInWorker(
        chart.metadata.name,
        {
          data: dataset,
          mapping,
          visualOptions,
          dataTypes,
        },
        chart.rawCustomChart
      )
    : viz._getVizData()
}

export async function buildCanvasForExport({
  chart,
  dataset,
  dataTypes,
  mapping,
  visualOptions,
  brandTheme,
  scale = 1,
}) {
  if (!chart || !dataset || !dataTypes || !canDirectCanvasExport(chart)) {
    return null
  }

  // For small datasets, let the SVG export path handle it — this ensures the
  // export matches the SVG preview and picks up all brand theme transforms.
  if (Array.isArray(dataset) && dataset.length < FAST_PREVIEW_ROW_THRESHOLD) {
    return null
  }

  const mappedData = await buildMappedData({
    chart,
    dataset,
    dataTypes,
    mapping,
    visualOptions,
  })

  // If the colorScale has no user-defined values, auto-build from the brand
  // palette so the canvas render matches what the brand theme would produce.
  function withBrandColorScale(options, colorValues) {
    if (options?.colorScale?.userScaleValues?.length) return options
    const domain = Array.from(
      new Set((colorValues || []).filter((v) => v !== undefined && v !== null))
    )
    return {
      ...options,
      colorScale: {
        ...options?.colorScale,
        ...buildBrandColorScaleOption(brandTheme, domain),
      },
    }
  }

  const canvas = document.createElement('canvas')

  if (chart.metadata.id === CHART_IDS.POINT_MAP) {
    const opts = withBrandColorScale(
      visualOptions,
      mappedData.map((p) => p.__pointMapColor)
    )
    renderPointMapCanvas(canvas, mappedData, opts, mapping, scale, brandTheme)
    return composeCanvasWithBrandHeader(canvas, opts, brandTheme)
  }

  if (chart.metadata.id === CHART_IDS.BEESWARM) {
    const opts = withBrandColorScale(
      visualOptions,
      mappedData.map((n) => n.color)
    )
    renderBeeswarmCanvas(canvas, mappedData, opts, mapping, brandTheme, scale)
    return composeCanvasWithBrandHeader(canvas, opts, brandTheme)
  }

  return null
}
